import { getIcon } from "@litonarefin/utils/icons/index";
import Link from "next/link";

const DOTS = "dots";

const range = (start, end) =>
    Array.from({ length: end - start + 1 }, (_, i) => start + i);

// Windowed page list: first / last always shown, `sibling` pages around the
// active one, ellipsis for the rest. Keeps the row narrow enough for phones
// instead of printing every page number.
const getPageItems = (totalPage, activePage, sibling = 1) => {
    const totalSlots = sibling * 2 + 5;

    if (totalPage <= totalSlots) return range(1, totalPage);

    const leftSibling = Math.max(activePage - sibling, 1);
    const rightSibling = Math.min(activePage + sibling, totalPage);

    const showLeftDots = leftSibling > 2;
    const showRightDots = rightSibling < totalPage - 1;

    if (!showLeftDots && showRightDots) {
        return [...range(1, sibling * 2 + 3), DOTS, totalPage];
    }

    if (showLeftDots && !showRightDots) {
        return [1, DOTS, ...range(totalPage - (sibling * 2 + 2), totalPage)];
    }

    return [1, DOTS, ...range(leftSibling, rightSibling), DOTS, totalPage];
};

const ARROW_CLASS =
    "jlt-py-3 jlt-px-3 sm:jlt-px-4 jlt-bg-[#fff] jlt-rounded-[2px] jlt-flex jlt-shrink-0 jlt-justify-center jlt-items-center jlt-min-w-[36px] sm:jlt-min-w-[44px]";

const PAGE_CLASS =
    "jlt-py-1.5 sm:jlt-py-3 jlt-px-2 sm:jlt-px-4 jlt-rounded-[2px] jlt-text-sm sm:jlt-text-base jlt-font-bold jlt-flex jlt-shrink-0 jlt-justify-center jlt-items-center jlt-min-w-[36px] sm:jlt-min-w-[44px] jlt-shadow-[0px_0px_10px_rgba(173,173,188,0.1)]";

export const Pagination = ({
    href,
    query = "",
    prevPage,
    setPrevPage,
    nextPage,
    setNextPage,
    activePage,
    setActivePage,
    totalPage,
}) => {
    if (!totalPage || totalPage < 2) return null;

    const pageItems = getPageItems(totalPage, activePage);

    return (
        <div className="jlt-flex jlt-flex-wrap jlt-gap-2 jlt-items-center jlt-justify-center jlt-max-w-full">
            {0 === prevPage ? null : (
                <Link
                    onClick={() => {
                        setActivePage(prevPage);
                        setNextPage(prevPage);
                        setPrevPage(prevPage - 1);
                    }}
                    aria-label="Previous page"
                    href={`/${href}/page/${prevPage}/${query ? query : ""}`}
                    className={ARROW_CLASS}>
                    <span className="jlt-rotate-180">
                        {getIcon("arrowRight")}
                    </span>
                </Link>
            )}

            {pageItems.map((page, index) => {
                if (page === DOTS) {
                    return (
                        <span
                            key={`dots-${index}`}
                            aria-hidden="true"
                            className="jlt-px-1 jlt-text-base jlt-font-bold jlt-text-[--primary] jlt-select-none">
                            &hellip;
                        </span>
                    );
                }

                if (activePage === page) {
                    return (
                        <a
                            aria-current="page"
                            className={`${PAGE_CLASS} jlt-text-white jlt-bg-[--primary] jlt-cursor-not-allowed`}
                            key={page}>
                            {page}
                        </a>
                    );
                }

                return (
                    <Link
                        href={
                            page === 1
                                ? `/${href}/${query ? query : ""}`
                                : `/${href}/page/${page}/${query ? query : ""}`
                        }
                        onClick={() => {
                            setNextPage(page);
                            setPrevPage(page - 1);
                            setActivePage(page);
                        }}
                        key={page}
                        className={`${PAGE_CLASS} jlt-text-[--primary] jlt-bg-white`}>
                        {page}
                    </Link>
                );
            })}

            {totalPage === nextPage ? null : (
                <Link
                    onClick={() => {
                        setActivePage(nextPage + 1);
                        setNextPage(nextPage + 1);
                        setPrevPage(nextPage);
                    }}
                    aria-label="Next page"
                    href={`/${href}/page/${nextPage + 1}/${query ? query : ""}`}
                    className={ARROW_CLASS}>
                    {getIcon("arrowRight")}
                </Link>
            )}
        </div>
    );
};
